import type {SurveyAnswer, SurveyFieldValue, SurveyFormValues, SurveyResponse, SurveySpec, SurveyStep} from './survey.types';
import {SurveyFieldKind} from './survey.types';

const OTHER_SUFFIX = '__other';

/** The form-field name that holds a step's free-text "other" entry. */
export function otherKey(key: string): string {
  return `${key}${OTHER_SUFFIX}`;
}

export function emptyValueForFieldKind(kind: SurveyFieldKind): SurveyFieldValue {
  if (kind === SurveyFieldKind.MULTISELECT) {
    return [];
  }

  return '';
}

export function presentText(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();

  if (trimmed === '') {
    return undefined;
  }

  return trimmed;
}

/** Returns the value when the field counts as answered (non-empty), otherwise undefined. */
export function presentAnswer(value: unknown): SurveyFieldValue | undefined {
  const text = presentText(value);

  if (text !== undefined) {
    return text;
  }

  if (Array.isArray(value) && value.length > 0) {
    return value as string[];
  }

  return undefined;
}

/**
 * Shape the raw react-hook-form values into the SurveyResponse contract: for each step,
 * keep the present answer and/or the trimmed free-text "other" entry, dropping steps that
 * have neither. Pure — the component passes in the current values and the spec's steps.
 */
export function buildResponse(values: SurveyFormValues, steps: SurveyStep[]): SurveyResponse {
  const out: SurveyResponse = {};

  steps.forEach((step) => {
    const answer = presentAnswer(values[step.key]);
    const other = presentText(values[otherKey(step.key)]);
    const entry: SurveyAnswer = {};

    if (answer !== undefined) {
      entry.value = answer;
    }
    if (other !== undefined) {
      entry.other = other;
    }
    if (entry.value !== undefined || entry.other !== undefined) {
      out[step.key] = entry;
    }
  });

  return out;
}

const optionLabelForValue = (step: SurveyStep, value: string): string => {
  if (step.field.kind === SurveyFieldKind.SELECT || step.field.kind === SurveyFieldKind.MULTISELECT) {
    return step.field.options.find((option) => option.value === value)?.label ?? value;
  }

  return value;
};

const formatSurveyAnswer = (step: SurveyStep, answer: SurveyAnswer): string => {
  const parts: string[] = [];

  if (answer.value !== undefined) {
    const values = Array.isArray(answer.value) ? answer.value : [answer.value];
    parts.push(values.map((value) => optionLabelForValue(step, value)).join(', '));
  }

  if (answer.other !== undefined && answer.other !== '') {
    parts.push(answer.other);
  }

  return parts.filter((part) => part !== '').join(', ');
};

export function renderSurveyResponseEcho(spec: SurveySpec, response: SurveyResponse): string {
  const blocks: string[] = [];

  for (const step of spec.steps) {
    const answer = response[step.key];
    if (!answer) {
      continue;
    }

    blocks.push(`**Q:** ${step.helpText}  \n**A:** ${formatSurveyAnswer(step, answer)}`);
  }

  if (blocks.length === 0) {
    return `> *${window.i18next.t('pmwjs_ai_chat_survey_skipped')}*`;
  }

  return blocks.join('\n\n');
}
